import { gql } from '@apollo/client';
import { Languages, useMutation, useTranslation } from '@gimlite/common';
import { zodResolver } from '@hookform/resolvers/zod';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useStoreUIV2 } from '../../hooks/useStore.hook';
import { UpdateProfileWizard } from '../../wizards/updateProfile/updateProfile.wizard';
import { Alert } from '../alert/alert.component';
import { Avatar } from '../avatar/avatar.component';
import { Button } from '../button/button.component';
import { ButtonRounded } from '../buttonRounded/buttonRounded.component';
import { Col } from '../col/col.component';
import {
  Description,
  DescriptionGroup,
} from '../description/description.component';
import { Dialog } from '../dialog/dialog.component';
import { Drawer } from '../drawer/drawer.component';
import { Form, FormField, FormItem } from '../form/form.component';
import { Icon, IconType } from '../icon/icon.component';
import { InputPassword } from '../inputPassword/inputPassword.component';
import { PasswordStrengthValidator } from '../passwordStrengthValidator';
import { Popover } from '../popover/popover.component';
import { Select } from '../select/select.component';
import { Text, TextType } from '../text/text.component';
import { Zone, ZoneArea } from '../zone/zone.component';
import './myAccount.component.scss';

const variantMapper = {
  danger: {
    iconColor: 'alert_error',
    textColor: 'alert_error',
    textVariant: 'button-s',
  },
  default: {
    iconColor: 'black_90',
    textColor: 'black_70',
    textVariant: 'body',
  },
} as const satisfies Record<
  string,
  {
    iconColor: IconType.Props['color'];
    textColor: TextType.Props['color'];
    textVariant: TextType.Props['variant'];
  }
>;

declare namespace MyAccountMenuItemType {
  type Props = {
    onClick?: () => void;
    label: string;
    iconLeft?: IconType.Props['variant'];
    iconRight?: IconType.Props['variant'];
    variant?: keyof typeof variantMapper;
  };
}

const MyAccountMenuItem = observer(
  ({
    onClick,
    label,
    iconLeft,
    iconRight,
    variant = 'default',
  }: MyAccountMenuItemType.Props) => {
    return (
      <div className="myAccountMenuItem" onClick={() => onClick?.()}>
        <div className="myAccountMenuItem__content">
          {iconLeft && (
            <Icon
              size="xs"
              variant={iconLeft}
              color={variantMapper[variant].iconColor}
            />
          )}
          <Text
            color={variantMapper[variant].textColor}
            variant={variantMapper[variant].textVariant}
          >
            {label}
          </Text>
        </div>
        {iconRight && (
          <Icon size="xs" variant={iconRight} color="deep_indigo_70" />
        )}
      </div>
    );
  },
);

declare namespace MyAccountMenuGroupType {
  type Props = {
    children: React.ReactNode | React.ReactNode[];
  };
}

const MyAccountMenuGroup = observer(
  ({ children }: MyAccountMenuGroupType.Props) => {
    const { t } = useTranslation();

    return <div className="myAccountMenuGroup">{children}</div>;
  },
);

declare namespace MyAccountContentType {
  type Props = {
    lastName?: string | null;
    firstName?: string | null;
    avatar?: string | null;
  };
}

const MyAccountContent = observer(
  ({ lastName, firstName, avatar }: MyAccountContentType.Props) => {
    const { t } = useTranslation();

    return (
      <div className="myAccountContent">
        <div className="myAccountContent__header">
          <Avatar
            size="medium"
            firstName={firstName}
            lastName={lastName}
            image={avatar}
          />
          <div className="myAccountContent__header__present">
            <Text color={'black_70'} variant="headline">
              {firstName} {lastName}
            </Text>
            {/* //! TODO: Faudra définir selon les rôles */}
            <Text color={'black_70'} variant="button-s">
              {t('watermelon-administrator')}
            </Text>
          </div>
        </div>
      </div>
    );
  },
);

declare namespace MyAccountTriggerType {
  type Props = {
    lastName?: string | null;
    firstName?: string | null;
    avatar?: string | null;
  };
}

const MyAccountTrigger = observer(
  ({ lastName, firstName, avatar }: MyAccountTriggerType.Props) => {
    return (
      <div className="myAccountTrigger">
        {(lastName || firstName) && (
          <div className="myAccountTrigger__content">
            <div className="myAccountTrigger__content__present">
              <Avatar
                firstName={firstName}
                lastName={lastName}
                image={avatar}
              />
              <Text color={'deep_indigo_70'} variant="subhead">
                {firstName} {lastName}
              </Text>
            </div>
            <Icon
              className={'myAccountTrigger__content__icon'}
              variant="dropdown"
              color={'deep_indigo_70'}
            />
          </div>
        )}
      </div>
    );
  },
);

const MyAccountInformation = observer(
  ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
    const { t } = useTranslation();
    const { SSOStore, WizardStore } = useStoreUIV2();

    return (
      <Drawer
        size="medium"
        subTitle={t('watermelon-profile')}
        title={t('watermelon-personalInformation')}
        isOpen={isOpen}
        onClose={onClose}
        extra={[
          <ButtonRounded
            key="edit"
            variant="outline"
            icon="edit"
            onClick={() => {
              // Toujours ouvrir un nouveau wizard pour l'édition de profil
              WizardStore.openNew({ id: 'updateProfile' });
            }}
          ></ButtonRounded>,
        ]}
      >
        <Zone
          zones={[['main']]}
          rows={['min-content']}
          columns={['1fr']}
          gapY={24}
        >
          <ZoneArea area="main">
            <DescriptionGroup
              columns={2}
              title={t('watermelon-mainInformations')}
              extra={
                <Avatar
                  size="small"
                  firstName={SSOStore?.account?.firstName}
                  lastName={SSOStore?.account?.lastName}
                  image={SSOStore?.account?.avatar}
                />
              }
            >
              <Description
                label={t('watermelon-lastName')}
                value={SSOStore?.account?.lastName}
              />
              <Description
                label={t('watermelon-firstName')}
                value={SSOStore?.account?.firstName}
              />
              <Description
                label={t('watermelon-email')}
                value={SSOStore?.account?.email}
              />
              <Description
                label={t('watermelon-username')}
                value={SSOStore?.account?.username}
              />
            </DescriptionGroup>
          </ZoneArea>
        </Zone>
      </Drawer>
    );
  },
);

type FormPasswordType = {
  currentPassword?: string | null;
  newPassword?: string | null;
  confirmNewPassword?: string | null;
};

const MyAccountSecurity = observer(() => {
  const { t } = useTranslation();
  const { SSOStore, GQLStore, NotificationStore } = useStoreUIV2();
  const [touchedPassword, setTouchedPassword] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);

  const [updatePasswordCall, updatePasswordState] = useMutation<
    {
      updatePassword: { success: boolean };
    },
    {
      input: {
        currentPassword: string;
        newPassword: string;
      };
    }
  >(
    gql`
      mutation updatePassword($input: UpdatePasswordDto!) {
        updatePassword(input: $input) {
          success
        }
      }
    `,
    {
      notification: {
        success: t('watermelon-passwordUpdated'),
        error: t('watermelon-passwordUpdateError'),
      },
    },
  );

  const FormPasswordSchema = z
    .object({
      currentPassword: z.string().trim().nullable().optional(),
      newPassword: z.string().trim().nullable().optional(),
      confirmNewPassword: z.string().trim().nullable().optional(),
    })
    .refine(
      (data) => {
        if (data.newPassword && data.confirmNewPassword) {
          return data.newPassword === data.confirmNewPassword;
        }
        return true;
      },
      {
        message: t('watermelon-inconsistencyPasswordDescription'),
        path: ['confirmNewPassword'],
      },
    );

  const formPassword = useForm<FormPasswordType>({
    resolver: zodResolver(FormPasswordSchema),
    defaultValues: {
      confirmNewPassword: null,
      newPassword: null,
      currentPassword: null,
    },
    mode: 'onTouched',
  });

  useEffect(() => {
    const subscription = formPassword.watch((value, { name, type }) => {
      if (name === 'confirmNewPassword' && value.newPassword) {
        formPassword.trigger('confirmNewPassword');
      }
    });

    return () => subscription.unsubscribe();
  }, [formPassword]);

  useEffect(() => {
    return () => {
      updatePasswordState.reset();
      formPassword.reset();
      setTouchedPassword(false);
      setIsPasswordValid(false);
    };
  }, []);

  return (
    <Drawer
      onClose={() => {
        updatePasswordState.reset();
        formPassword.reset();
        setTouchedPassword(false);
        setIsPasswordValid(false);
      }}
      size="medium"
      subTitle={t('watermelon-profile')}
      title={t('watermelon-securitySettings')}
      triggerElement={
        <MyAccountMenuItem
          onClick={() => {}}
          label={t('watermelon-securitySettings')}
          iconLeft="setting"
        />
      }
    >
      <Zone
        zones={[['password'], SSOStore?.otpIsActive ? ['2fa'] : []].filter(
          (zone) => zone.length > 0,
        )}
        rows={
          [
            'min-content',
            SSOStore?.otpIsActive ? 'min-content' : undefined,
          ].filter(Boolean) as string[]
        }
        columns={['1fr']}
        gapY={24}
      >
        <ZoneArea area="password">
          <DescriptionGroup
            columns={2}
            title={t('watermelon-password')}
            expandable={true}
          >
            <Form {...formPassword}>
              <FormField
                control={formPassword.control}
                name="currentPassword"
                render={({ field }) => (
                  <FormItem
                    columnExtend={1}
                    label={t('watermelon-currentPassword')}
                  >
                    <InputPassword {...field} />
                  </FormItem>
                )}
              />

              <div></div>

              <FormField
                control={formPassword.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem
                    columnExtend={1}
                    label={t('watermelon-newPassword')}
                  >
                    <Col gap={12}>
                      <InputPassword
                        {...field}
                        onChange={(value) => {
                          setTouchedPassword(true);
                          field.onChange(value);
                        }}
                      />
                      <PasswordStrengthValidator
                        password={field.value}
                        touched={touchedPassword}
                        minLength={12}
                        onChange={(result) => {
                          setIsPasswordValid(result.isValid);
                        }}
                      />
                    </Col>
                  </FormItem>
                )}
              />
              <div></div>

              <FormField
                control={formPassword.control}
                name="confirmNewPassword"
                render={({ field }) => (
                  <FormItem
                    columnExtend={1}
                    label={t('watermelon-confirmNewPassword')}
                  >
                    <InputPassword {...field} />
                  </FormItem>
                )}
              />

              <div></div>

              <Button
                disabled={
                  !formPassword.getValues('currentPassword') ||
                  !formPassword.getValues('newPassword') ||
                  !formPassword.getValues('confirmNewPassword') ||
                  !formPassword.formState.isValid ||
                  !isPasswordValid
                }
                width="min-content"
                onClick={() => {
                  updatePasswordCall({
                    variables: {
                      input: {
                        currentPassword:
                          formPassword.getValues('currentPassword')!,
                        newPassword: formPassword.getValues('newPassword')!,
                      },
                    },
                  });
                }}
              >
                {t('watermelon-validate')}
              </Button>
            </Form>
          </DescriptionGroup>
        </ZoneArea>
        {SSOStore?.otpIsActive && (
          <ZoneArea area="2fa">
            <DescriptionGroup
              columns={2}
              title={t('watermelon-authentication2FA')}
              extra={[
              
                <Button
                  variant="ghost"
                  iconRight="refresh"
                  
                  onClick={() => {
                    SSOStore?.activeOtp({
                      client: GQLStore.client!,
                      introspection: GQLStore.introspection!,
                    });
                  }}
                >
                  {t('watermelon-toUpdate')}
                </Button>,
              ]}
            ></DescriptionGroup>
          </ZoneArea>
        )}
      </Zone>
    </Drawer>
  );
});

const flagMapper = {
  fr_FR: 'FRA',
  en_EN: 'GBR',
  de_DE: 'DEU',
  it_IT: 'ITA',
} as const satisfies Record<Languages, IconType.Props['variant']>;

const MyAccountLang = observer(() => {
  const { SSOStore, I18NStore, GQLStore, NotificationStore } = useStoreUIV2();

  const { lang, t } = useTranslation();
  const [langSelected, setLangSelected] = useState<Languages | null>(null);

  return (
    <Dialog
      onConfirm={async () => {
        if (!langSelected) return;

        try {
          await I18NStore.updateLang({
            client: GQLStore.client!,
            introspection: GQLStore.introspection!,
            lang: langSelected || lang!,
          });
          NotificationStore.emit({
            mode: 'SUCCESS',
            content: t('watermelon-langUpdated'),
          });
        } catch (error) {
          console.error(error);
          NotificationStore.emit({
            mode: 'ERROR',
            content: t('watermelon-langUpdateError'),
          });
        }
      }}
      customContent={
        <Select
          value={langSelected || lang}
          onChange={(value) => {
            setLangSelected(value as Languages);
          }}
          options={[
            { label: t('watermelon-fr_FR-untranslated'), value: 'fr_FR' },
            { label: t('watermelon-en_EN-untranslated'), value: 'en_EN' },
            { label: t('watermelon-de_DE-untranslated'), value: 'de_DE' },
            { label: t('watermelon-it_IT-untranslated'), value: 'it_IT' },
          ]}
        />
      }
      icon="globe"
      variant="basic"
      title={t('watermelon-choiceLanguage')}
      mode="action"
    >
      <MyAccountMenuItem
        variant="default"
        label={t('watermelon-LANGUAGE_CURRENT')}
        iconLeft="globe"
        iconRight={flagMapper[langSelected || lang!]}
      />
    </Dialog>
  );
});

export declare namespace MyAccountType {
  type Props = {
    className?: string | null;
  };
}

export const MyAccount = observer(({ className }: MyAccountType.Props) => {
  const { SSOStore } = useStoreUIV2();
  const account = SSOStore?.account;
  const { t } = useTranslation();
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const { WizardStore } = useStoreUIV2();

  useEffect(() => {
    if (WizardStore.load) {
      const isAlreadyRegistered = WizardStore.registry.some(
        (wizard) => wizard.id === 'updateProfile',
      );

      if (!isAlreadyRegistered) {
        WizardStore.init({
          registry: [
            {
              id: 'updateProfile',
              component: UpdateProfileWizard,
            },
          ],
        });
      }
    }
  }, [WizardStore]);

  return (
    <div className={classNames('myAccount', className)}>
      <Popover
        withPadding={false}
        triggerEvent="click"
        triggerElement={
          <MyAccountTrigger
            lastName={account?.lastName}
            firstName={account?.firstName}
            avatar={account?.avatar}
          />
        }
      >
        <div className="myAccount__body">
          <MyAccountContent
            lastName={account?.lastName}
            firstName={account?.firstName}
            avatar={account?.avatar}
          />
          <MyAccountMenuGroup>
            <MyAccountMenuItem
              onClick={() => setIsInfoOpen(true)}
              label={t('watermelon-personalInformation')}
              iconLeft="profil"
            />
            <MyAccountLang />
            <MyAccountSecurity />
            <MyAccountMenuItem
              variant="danger"
              onClick={() => {
                SSOStore.logout();
              }}
              label={t('watermelon-logout')}
              iconLeft="logout"
            />
          </MyAccountMenuGroup>
        </div>
      </Popover>
      <MyAccountInformation
        isOpen={isInfoOpen}
        onClose={() => setIsInfoOpen(false)}
      />
    </div>
  );
});
